import { apiClient, clearApiQuery, refetchApiQuery } from "../../services/api";

/**
 * Write the practitioner's answer about their practice, and refresh every read
 * of it.
 *
 * The two halves are ONE operation, which is why they are one function. Two
 * screens write this answer — the signup slide and the settings editor — and
 * three cached reads describe it. While only one of them refreshed those reads,
 * signup left `GET /practice-profiles/me` holding the pre-answer
 * `confirmedAt: null` for the rest of the session, and the Explore gate asked a
 * practitioner for a practice they had given one slide earlier. "Whoever writes
 * this refreshes it" has to live in one place, or it is a rule only one caller
 * obeys.
 *
 * The failure is RETURNED, not thrown: the two callers report it differently
 * (a toast in settings, a swallowed step in the signup carousel) and both must
 * be able to tell a save that landed from one that did not — signup gates its
 * confirmation event on exactly that.
 *
 * The refreshes are RE-READS, not invalidations, and the difference is the whole
 * reason this function was not enough the first time. `invalidateQueries` can be
 * answered by an identical GET that left before the PUT and has not resolved yet
 * (`refetchApiQuery` carries the mechanism): the await then settles on the
 * pre-write body and nothing new is requested. That is precisely what happened
 * here — the Explore gate's own `GET /me` leaves the moment the account exists,
 * which is the same instant signup's finalize writes this answer, so the gate
 * went on reading `confirmedAt: null` for the rest of the session and re-asked a
 * practitioner who had answered one slide earlier.
 *
 * And they are AWAITED: a caller that navigates straight into a surface reading
 * these keys would otherwise race its own write and be served the answer it just
 * replaced.
 */
export async function savePracticeProfiles(answer: {
  readonly profileUids: readonly string[];
  /** `[]` is "no side named" — the axis question travels with the practices. */
  readonly disambiguation: readonly string[];
  /**
   * The practitioner's own words for a practice the catalogue does not carry,
   * `null` when it did. Required of both callers, and of a settings save the
   * flag left no field on: the PUT replaces this field, so a caller that
   * omitted it would clear a declaration it was never shown.
   */
  readonly declaration: string | null;
}): Promise<{ status: "saved" } | { status: "failed" }> {
  const written = await apiClient
    .put("/webapp/private/practice-profiles/me", {
      json: {
        profileUids: [...answer.profileUids],
        disambiguation: [...answer.disambiguation],
        declaration: answer.declaration,
      },
    })
    .then(() => ({ status: "saved" }) as const)
    // Turned into a value rather than propagated: the caller owns how this is
    // told to the practitioner, and it is already captured by onRequestError.
    .catch(() => ({ status: "failed" }) as const);
  if (written.status === "failed") return written;
  await refreshPracticeAnswerQueries();
  return written;
}

/**
 * Re-read, or drop, every cached read of the practice answer. Its own function
 * because a second write changes that answer: a change of main specialty makes
 * the server delete the practices held (`saveMainSpecialty`).
 */
export async function refreshPracticeAnswerQueries(): Promise<void> {
  // A re-read can itself fail, and that is NOT a failed save: the PUT returned
  // 204, the answer is stored, and reporting otherwise would tell the signup
  // carousel to leave the account unstamped and re-ask. What must not survive is
  // the pre-write body — it is KNOWN stale now, and a stale body is exactly what
  // the Explore gate mistakes for an answer. Dropped, so the next read is
  // `pending`: the gate defers on that, where it would have redirected on the
  // stale one. This policy lives HERE and not in the primitive because it is
  // this caller that knows a gate reads these keys.
  // One try per key, not one around both: a failure is already reported by
  // onRequestError, so all that is decided here is what the cache may keep — and
  // a body that just landed fresh is not a casualty of the OTHER key failing.
  try {
    await refetchApiQuery("/webapp/private/practice-profiles/me", {});
  } catch {
    clearApiQuery("/webapp/private/practice-profiles/me", {});
  }
  // The rail reads the catalogue endpoint, which is a different key — and it is
  // the surface the practitioner will look at to see whether this worked.
  try {
    await refetchApiQuery("/webapp/private/practice-profiles", {});
  } catch {
    clearApiQuery("/webapp/private/practice-profiles", {});
  }
  // Every read that describes this answer, DROPPED rather than re-read. `{}`
  // matches every page of every practice in every language, and a prefix has no
  // single request that answers it — there is nothing to re-read, and what
  // changed is which feeds exist at all: a feed the reader just unchecked must
  // not survive in the cache behind a chip that no longer exists. The next mount
  // fetches them.
  //
  // ⚠️ THIS LIST IS THE RULE THIS FILE EXISTS FOR. The MeSH rail shipped four
  // more reads of this same answer and declared none of them here, which is
  // exactly the failure the header above was written about: the aggregate rail
  // went on drawing chips for a practice the reader had just dropped. Adding a
  // cached read of the practitioner's practices without adding it here is how
  // that happens again.
  //
  // The two AGGREGATE keys are the load-bearing ones. They carry no `:uid`, so
  // nothing about their key changes when the held set does — they are stale with
  // no outward sign, where a per-practice key at least stops being asked for.
  clearApiQuery("/webapp/private/practice-profiles/publications", {});
  clearApiQuery("/webapp/private/practice-profiles/rail-terms", {});
  clearApiQuery("/webapp/private/practice-profiles/:uid/publications", {});
  clearApiQuery("/webapp/private/practice-profiles/:uid/rail-terms", {});
  clearApiQuery("/webapp/private/practice-profiles/:uid/filter-values", {});
}
